var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Accordion, Modal, ModalFooter, Button, Loading, } from "@folio/stripes/components";
import { useAgencyTitleDetail } from "../../hooks/useAgencyTitleDetail";
import { InfoBox } from "../InfoBox";
import { formatIsbn } from "../../utils/isbn";
import styles from "./AgencyTitleConfirmDialog.module.css";
var hasValue = function (value) {
    return Boolean(value === null || value === void 0 ? void 0 : value.trim());
};
var joinValues = function (values) {
    return values.filter(hasValue).join(", ");
};
var joinParts = function (values) {
    return values.filter(hasValue).join(" ");
};
var DetailSection = function (_a) {
    var fields = _a.fields, label = _a.label, columns = _a.columns, emptyText = _a.emptyText;
    var gridClassName = {
        one: styles.recordGridOne,
        two: styles.recordGridTwo,
        three: styles.recordGridThree,
    }[columns];
    return (React.createElement("section", { className: styles.recordSection },
        React.createElement("h3", { className: styles.sectionLabel }, label),
        fields.length === 0 ? (React.createElement("div", { className: styles.recordEmptyNote }, emptyText)) : (React.createElement("div", { className: gridClassName }, fields.map(function (field) { return (React.createElement("div", { className: styles.recordField, key: field.key },
            React.createElement("div", { className: styles.recordFieldLabel }, field.label),
            React.createElement("div", { className: field.isEmpty
                    ? "".concat(styles.recordFieldValue, " ").concat(styles.recordFieldValueEmpty)
                    : styles.recordFieldValue }, field.value))); })))));
};
export var AgencyTitleConfirmDialog = function (_a) {
    var open = _a.open, agencyTitle = _a.agencyTitle, onConfirm = _a.onConfirm, onReject = _a.onReject, onClose = _a.onClose, _b = _a.isLoading, isLoading = _b === void 0 ? false : _b;
    var _c = useIntl(), formatDate = _c.formatDate, formatMessage = _c.formatMessage, formatNumber = _c.formatNumber;
    var _d = useState(false), expanded = _d[0], setExpanded = _d[1];
    var bodyRef = useRef(null);
    var productNumber = agencyTitle === null || agencyTitle === void 0 ? void 0 : agencyTitle.productNumber;
    var _e = useAgencyTitleDetail(productNumber, expanded), data = _e.data, isDetailLoading = _e.isLoading, isError = _e.isError;
    useEffect(function () {
        if (!open) {
            setExpanded(false);
            return undefined;
        }
        // Modal autofocuses its first tabbable (the record toggle) — move initial focus to the neutral body
        var frame = requestAnimationFrame(function () { var _a; return (_a = bodyRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
        return function () { return cancelAnimationFrame(frame); };
    }, [open]);
    var handleToggle = useCallback(function () {
        setExpanded(function (current) { return !current; });
    }, []);
    var emptyText = formatMessage({
        id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.empty",
    });
    var recordSections = useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        if (!data)
            return null;
        var fieldLabel = function (id) {
            return formatMessage({
                id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.field.".concat(id),
            });
        };
        var detailField = function (key, label, value) { return ({
            key: key,
            label: label,
            value: hasValue(value) ? value : emptyText,
            isEmpty: !hasValue(value),
        }); };
        var copyright = data.copyrightFrom !== null && data.copyrightFrom !== undefined
            ? data.copyrightTo && data.copyrightTo !== data.copyrightFrom
                ? "\u00A9 ".concat(data.copyrightFrom, "\u2013").concat(data.copyrightTo)
                : "\u00A9 ".concat(data.copyrightFrom)
            : "";
        var dimensions = [
            (_a = data.measures) === null || _a === void 0 ? void 0 : _a.height,
            (_b = data.measures) === null || _b === void 0 ? void 0 : _b.width,
            (_c = data.measures) === null || _c === void 0 ? void 0 : _c.thickness,
        ].filter(function (value) { return value !== null && value !== undefined; });
        var measureParts = [
            dimensions.length > 0 ? "".concat(dimensions.join(" × "), " mm") : "",
            ((_d = data.measures) === null || _d === void 0 ? void 0 : _d.weight) !== null && ((_e = data.measures) === null || _e === void 0 ? void 0 : _e.weight) !== undefined
                ? "".concat(data.measures.weight, " g")
                : "",
        ].filter(hasValue);
        var priceParts = [
            joinParts([
                ((_f = data.price) === null || _f === void 0 ? void 0 : _f.amount) !== null && ((_g = data.price) === null || _g === void 0 ? void 0 : _g.amount) !== undefined
                    ? formatNumber(data.price.amount)
                    : "",
                (_h = data.price) === null || _h === void 0 ? void 0 : _h.currency,
            ]),
            (_j = data.price) === null || _j === void 0 ? void 0 : _j.type,
        ].filter(hasValue);
        var ageRange = data.ageFrom !== null && data.ageFrom !== undefined
            ? data.ageTo !== null &&
                data.ageTo !== undefined &&
                data.ageTo !== data.ageFrom
                ? "".concat(data.ageFrom, "\u2013").concat(data.ageTo)
                : "".concat(data.ageFrom)
            : "";
        var editionFields = [
            detailField("editionNumber", fieldLabel("editionNumber"), data.editionNumber !== null && data.editionNumber !== undefined
                ? "".concat(data.editionNumber, ".")
                : null),
            detailField("editionStatement", fieldLabel("editionStatement"), data.editionStatement),
            detailField("publishingDate", fieldLabel("publishingDate"), data.publishingDate),
            detailField("imprint", fieldLabel("imprint"), data.imprint),
            detailField("copyright", fieldLabel("copyright"), copyright),
            detailField("publishers", fieldLabel("publishers"), joinValues(((_k = data.publishers) !== null && _k !== void 0 ? _k : []).map(function (publisher) {
                return hasValue(publisher.name)
                    ? hasValue(publisher.role)
                        ? "".concat(publisher.name, " (").concat(publisher.role, ")")
                        : publisher.name
                    : null;
            }))),
            detailField("publicationPlaces", fieldLabel("publicationPlaces"), joinValues((_l = data.publicationPlaces) !== null && _l !== void 0 ? _l : [])),
            detailField("collections", fieldLabel("collections"), joinValues((_m = data.collections) !== null && _m !== void 0 ? _m : [])),
            detailField("publishingStatus", fieldLabel("publishingStatus"), data.publishingStatus),
            detailField("doi", fieldLabel("doi"), data.doi),
        ];
        var contributorFields = ((_o = data.contributors) !== null && _o !== void 0 ? _o : [])
            .filter(function (contributor) { return hasValue(contributor.name); })
            .map(function (contributor, contributorIndex) {
            var _a;
            var roles = joinValues((_a = contributor.roles) !== null && _a !== void 0 ? _a : []);
            return {
                key: "contributor-".concat(contributor.name, "-").concat(roles),
                label: "".concat(contributorIndex + 1, ". ").concat(contributor.name),
                value: hasValue(roles) ? roles : emptyText,
                isEmpty: !hasValue(roles),
            };
        });
        var languageFields = ((_p = data.languages) !== null && _p !== void 0 ? _p : [])
            .map(function (language) {
            if (!hasValue(language.language))
                return null;
            return {
                key: "language-".concat(language.role, "-").concat(language.language),
                label: hasValue(language.role)
                    ? language.role
                    : fieldLabel("language"),
                value: language.language,
                isEmpty: false,
            };
        })
            .filter(function (field) { return field !== null; });
        var subjectFields = ((_q = data.subjects) !== null && _q !== void 0 ? _q : [])
            .map(function (subject) {
            var _a;
            var value = joinParts([subject.code, subject.text]);
            if (!value)
                return null;
            return {
                key: "subject-".concat((_a = subject.scheme) !== null && _a !== void 0 ? _a : "", "-").concat(value),
                label: hasValue(subject.scheme)
                    ? subject.scheme
                    : fieldLabel("subject"),
                value: value,
                isEmpty: false,
            };
        })
            .filter(function (field) { return field !== null; });
        var languageAndSubjectFields = __spreadArray(__spreadArray(__spreadArray([], (languageFields.length > 0
            ? languageFields
            : [detailField("language", fieldLabel("language"), null)]), true), (subjectFields.length > 0
            ? subjectFields
            : [detailField("subject", fieldLabel("subject"), null)]), true), [
            detailField("ageRange", fieldLabel("ageRange"), ageRange),
            detailField("audiences", fieldLabel("audiences"), joinValues((_r = data.audiences) !== null && _r !== void 0 ? _r : [])),
        ], false);
        var physicalFields = __spreadArray([
            detailField("pageCount", fieldLabel("pageCount"), data.pageCount !== null && data.pageCount !== undefined
                ? formatNumber(data.pageCount)
                : null),
            detailField("productForm", fieldLabel("productForm"), data.productForm),
            detailField("formDetails", fieldLabel("formDetails"), joinValues((_s = data.formDetails) !== null && _s !== void 0 ? _s : [])),
            detailField("primaryContentType", fieldLabel("primaryContentType"), data.primaryContentType),
            detailField("illustrationsNote", fieldLabel("illustrationsNote"), data.illustrationsNote),
            detailField("measures", fieldLabel("measures"), measureParts.join(" · ")),
            detailField("price", fieldLabel("price"), priceParts.join(" — "))
        ], ((_t = data.otherExtents) !== null && _t !== void 0 ? _t : [])
            .map(function (extent) {
            var value = joinParts([
                extent.value !== null && extent.value !== undefined
                    ? formatNumber(extent.value)
                    : "",
                extent.unit,
            ]);
            if (!value)
                return null;
            var label = hasValue(extent.type)
                ? extent.type
                : fieldLabel("otherExtent");
            return {
                key: "extent-".concat(label, "-").concat(value),
                label: label,
                value: value,
                isEmpty: false,
            };
        })
            .filter(function (field) { return field !== null; }), true);
        var textContentFields = ((_u = data.textContents) !== null && _u !== void 0 ? _u : [])
            .map(function (textContent) {
            var _a;
            if (!hasValue(textContent.text))
                return null;
            return {
                key: "text-content-".concat((_a = textContent.type) !== null && _a !== void 0 ? _a : "", "-").concat(textContent.text),
                label: hasValue(textContent.type)
                    ? textContent.type
                    : fieldLabel("textContent"),
                value: textContent.text,
                isEmpty: false,
            };
        })
            .filter(function (field) { return field !== null; });
        return {
            editionFields: editionFields,
            contributorFields: contributorFields,
            languageAndSubjectFields: languageAndSubjectFields,
            physicalFields: physicalFields,
            textContentFields: textContentFields,
        };
    }, [data, emptyText, formatMessage, formatNumber]);
    var panelTitle = (data === null || data === void 0 ? void 0 : data.dateUpdated)
        ? formatMessage({
            id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.panelTitleUpdated",
        }, { date: formatDate(data.dateUpdated) })
        : formatMessage({
            id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.panelTitle",
        });
    return (React.createElement(Modal, { open: open, id: "agency-title-confirm-modal", label: formatMessage({
            id: "ui-br-legaldeposits.enforcement.agencyConfirm.title",
        }), onClose: onClose, size: expanded ? "large" : "small", footer: React.createElement(ModalFooter, null,
            React.createElement(Button, { buttonStyle: "primary", onClick: onConfirm, disabled: isLoading, id: "agency-confirm-accept" }, formatMessage({
                id: "ui-br-legaldeposits.enforcement.agencyConfirm.confirm",
            })),
            React.createElement(Button, { onClick: onReject, disabled: isLoading, id: "agency-confirm-reject" }, formatMessage({
                id: "ui-br-legaldeposits.enforcement.agencyConfirm.reject",
            }))) },
        React.createElement("div", { ref: bodyRef, tabIndex: -1, className: styles.dialogBody },
            React.createElement("p", null, formatMessage({
                id: "ui-br-legaldeposits.enforcement.agencyConfirm.intro",
            })),
            agencyTitle && (React.createElement("dl", { className: styles.definitionList },
                React.createElement("dt", null, formatMessage({
                    id: "ui-br-legaldeposits.enforcement.agencyConfirm.field.name",
                })),
                React.createElement("dd", null, agencyTitle.name),
                agencyTitle.author && (React.createElement(React.Fragment, null,
                    React.createElement("dt", null, formatMessage({
                        id: "ui-br-legaldeposits.enforcement.agencyConfirm.field.author",
                    })),
                    React.createElement("dd", null, agencyTitle.author))),
                agencyTitle.publisherName && (React.createElement(React.Fragment, null,
                    React.createElement("dt", null, formatMessage({
                        id: "ui-br-legaldeposits.enforcement.agencyConfirm.field.publisher",
                    })),
                    React.createElement("dd", null, agencyTitle.publisherName))),
                agencyTitle.productNumber && (React.createElement(React.Fragment, null,
                    React.createElement("dt", null, formatMessage({
                        id: "ui-br-legaldeposits.enforcement.agencyConfirm.field.productNumber",
                    })),
                    React.createElement("dd", null, formatIsbn(agencyTitle.productNumber)))))),
            productNumber && (React.createElement("div", { className: styles.recordAccordion },
                React.createElement(Accordion, { id: "agency-record-accordion", label: panelTitle, open: expanded, onToggle: handleToggle },
                    expanded && isDetailLoading && (React.createElement("div", { className: styles.loadingRow },
                        React.createElement(Loading, null),
                        React.createElement("span", null, formatMessage({
                            id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.loading",
                        })))),
                    expanded && isError && (React.createElement("div", { className: styles.errorRow, role: "alert" }, formatMessage({
                        id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.error",
                    }))),
                    expanded && data && recordSections && (React.createElement("div", { className: styles.recordPanel },
                        React.createElement("div", { className: styles.recordBody },
                            React.createElement(DetailSection, { label: formatMessage({
                                    id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.section.edition",
                                }), fields: recordSections.editionFields, columns: "three", emptyText: emptyText }),
                            React.createElement(DetailSection, { label: formatMessage({
                                    id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.section.contributors",
                                }), fields: recordSections.contributorFields, columns: "three", emptyText: emptyText }),
                            React.createElement(DetailSection, { label: formatMessage({
                                    id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.section.languagesAndSubjects",
                                }), fields: recordSections.languageAndSubjectFields, columns: "three", emptyText: emptyText }),
                            React.createElement(DetailSection, { label: formatMessage({
                                    id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.section.physicalDescription",
                                }), fields: recordSections.physicalFields, columns: "three", emptyText: emptyText }),
                            React.createElement(DetailSection, { label: formatMessage({
                                    id: "ui-br-legaldeposits.enforcement.agencyConfirm.record.section.annotation",
                                }), fields: recordSections.textContentFields, columns: "one", emptyText: emptyText }))))))),
            React.createElement("div", { className: styles.consequences },
                React.createElement(InfoBox, { titleId: "ui-br-legaldeposits.enforcement.agencyConfirm.consequences.intro" },
                    React.createElement("ul", { className: styles.consequencesList },
                        React.createElement("li", null,
                            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.agencyConfirm.consequences.takeover" })),
                        React.createElement("li", null,
                            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.agencyConfirm.consequences.publish" }))))))));
};
